import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { useAuth } from "@/hooks/useAuth";
import { useAppSettings } from "@/hooks/useAppSettings";
import Dashboard from "@/pages/dashboard";
import Landing from "@/pages/landing";
import NotFound from "@/pages/not-found";
import StationSettings from "@/pages/station-settings";
import AdminSettings from "@/pages/admin";
import VBREngine from "@/pages/vbr-engine";
import CopyEngine from "@/pages/copy-engine";
import Leaderboard from "@/pages/leaderboard";
import PasswordReset from "@/pages/password-reset";
import UsageAnalytics from "@/pages/usage-analytics";
import Workbench from "@/pages/workbench";
import SponsorWorkbench from "@/pages/sponsor-workbench";

function Router() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { data: appSettings, isLoading: appSettingsLoading } = useAppSettings();

  // Check if user needs to reset password
  const needsPasswordReset = user?.requirePasswordReset === true;

  // App-wide rollout flag: when on, the classic view (dashboard + station
  // tool pages) is hidden for everyone and those routes land in the
  // Sponsor Workbench. Admin area and leaderboard stay reachable.
  const classicDisabled = appSettings?.disableClassicView === true;

  return (
    <Switch>
      {isLoading || (isAuthenticated && appSettingsLoading) ? (
        // Show nothing while loading
        <Route path="*">{() => null}</Route>
      ) : !isAuthenticated ? (
        // Not authenticated - show landing/login
        <Route path="/" component={Landing} />
      ) : needsPasswordReset ? (
        // Authenticated but needs password reset
        <>
          <Route path="/password-reset" component={PasswordReset} />
          {/* Redirect all other paths to password reset */}
          <Route path="*">
            {() => <Redirect to="/password-reset" />}
          </Route>
        </>
      ) : (
        // Authenticated and no password reset needed
        <>
          {classicDisabled ? (
            <>
              <Route path="/">{() => <Redirect to="/workbench" />}</Route>
              <Route path="/dashboard">{() => <Redirect to="/workbench" />}</Route>
            </>
          ) : (
            <>
              <Route path="/" component={Dashboard} />
              <Route path="/dashboard" component={Dashboard} />
            </>
          )}
          {/* Admin section. Bare /admin redirects to the default section so
              URLs always reflect which section is being viewed. The standalone
              admin pages (usage) must be matched BEFORE the generic
              /admin/:section so they aren't shadowed. */}
          <Route path="/admin">
            {() => <Redirect to="/admin/stations" />}
          </Route>
          <Route path="/admin/usage" component={UsageAnalytics} />
          <Route path="/admin/:section" component={AdminSettings} />
          <Route path="/leaderboard" component={Leaderboard} />
          {/* Sponsor Workbench: the "new experience", available to all users. */}
          <Route path="/workbench" component={Workbench} />
          <Route path="/workbench/sponsors/:sponsorId" component={SponsorWorkbench} />
          {/* Station settings is an ADMIN page (Admin > Stations > Manage),
              not part of the classic view - routed in both toggle states. */}
          <Route path="/stations/:id/settings" component={StationSettings} />
          {classicDisabled ? (
            <>
              {/* Classic station TOOL pages are shut off: they land in the
                  workbench. Explicit routes - wouter wildcards do not match
                  multi-segment paths reliably. */}
              <Route path="/stations/:stationId/vbr">{() => <Redirect to="/workbench" />}</Route>
              <Route path="/stations/:stationId/copy">{() => <Redirect to="/workbench" />}</Route>
              <Route path="/stations/:stationId">{() => <Redirect to="/workbench" />}</Route>
            </>
          ) : (
            <>
              {/* Station tool routes */}
              <Route path="/stations/:stationId/vbr" component={VBREngine} />
              <Route path="/stations/:stationId/copy" component={CopyEngine} />
              {/* Redirect from base station URL to VBR Engine */}
              <Route path="/stations/:stationId">
                {(params) => <Redirect to={`/stations/${params.stationId}/vbr`} />}
              </Route>
            </>
          )}
          {/* Redirect password-reset to dashboard after successful reset */}
          <Route path="/password-reset">
            {() => <Redirect to="/dashboard" />}
          </Route>
        </>
      )}
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
